import { cn } from '@/lib/utils'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { cva } from 'class-variance-authority'
import { ChevronDownIcon } from 'lucide-react'
import * as React from 'react'

type AccordionVariant = 'default' | 'outlined'

const AccordionVariantContext = React.createContext<AccordionVariant>('default')

const triggerVariants = cva(
    [
        'flex flex-1 items-center justify-between gap-4 rounded-md px-4 py-3',
        'text-left text-lg font-medium text-foreground',
        'cursor-pointer transition-colors outline-none',
        'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=open]:rounded-b-none [&[data-state=open]>svg]:rotate-180',
        'bg-primary/10 dark:bg-primary/20 outline-none border-none',
    ].join(' '),
    {
        variants: {
            variant: {
                default: 'dark:hover:bg-primary/40 hover:bg-primary/30',
                outlined: 'border bg-card hover:bg-accent/50',
            },
        },
        defaultVariants: { variant: 'default' },
    },
)

const contentVariants = cva(
    [
        'overflow-hidden text-sm text-foreground',
        'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
    ].join(' '),
    {
        variants: {
            variant: {
                default: '',
                outlined: 'border border-t-0 shadow-sm',
            },
        },
        defaultVariants: { variant: 'default' },
    },
)

const itemVariants = cva('rounded-md', {
    variants: {
        variant: {
            default: 'dark:bg-primary/10',
            outlined: '',
        },
    },
    defaultVariants: { variant: 'default' },
})

function Root({
    variant = 'default',
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root> & {
    variant?: AccordionVariant
}) {
    return (
        <AccordionVariantContext.Provider value={variant}>
            <AccordionPrimitive.Root data-slot="accordion" {...props} />
        </AccordionVariantContext.Provider>
    )
}

function Item({
    className,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
    return (
        <AccordionPrimitive.Item
            data-slot="accordion-item"
            className={cn(
                itemVariants({
                    variant: React.useContext(AccordionVariantContext),
                }),
                className,
            )}
            {...props}
        />
    )
}

function Trigger({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
    const variant = React.useContext(AccordionVariantContext)

    return (
        <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
                data-slot="accordion-trigger"
                className={cn(triggerVariants({ variant }), className)}
                {...props}
            >
                {children}
                <ChevronDownIcon className="text-secondary-foreground pointer-events-none size-6 shrink-0 transition-transform duration-200" />
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    )
}

function Content({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
    const variant = React.useContext(AccordionVariantContext)

    return (
        <AccordionPrimitive.Content
            data-slot="accordion-content"
            className={contentVariants({ variant })}
            {...props}
        >
            <div className={cn('pt-0 pb-4', className)}>{children}</div>
        </AccordionPrimitive.Content>
    )
}

/**
 * Accordion no estilo de setas, com ícone de seta rotacionando ao abrir/fechar.
 *
 * Use `variant="outlined"` no `Root` para o estilo com borda (em vez do
 * padrão elevado/card que reflete o MUI).
 *
 * @example
 * ```tsx
 * <ArrowAccordion.Root type="single" collapsible>
 *   <ArrowAccordion.Item value="item-1">
 *     <ArrowAccordion.Trigger>
 *       Título do Accordion
 *     </ArrowAccordion.Trigger>
 *     <ArrowAccordion.Content>
 *       Conteúdo do Accordion
 *     </ArrowAccordion.Content>
 *   </ArrowAccordion.Item>
 * </ArrowAccordion.Root>
 * ```
 */
export const ArrowAccordion = {
    Root,
    Item,
    Trigger,
    Content,
}
